import { cn } from '@workspace/ui/lib/utils';
import { observer } from 'mobx-react-lite';
import type React from 'react';
import { cloneElement, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import Icon from '~/components/Icon/Icon';
import { useMounted } from '~/hooks/useMounted';
import { useSearchBar } from '~/hooks/useSearchBar';

import { SearchDropdownContent } from './components/SearchDropdownContent';

interface SearchBarProps
  extends Omit<React.ComponentPropsWithRef<'div'>, 'children'> {
  trigger?: React.ReactElement<React.ComponentPropsWithRef<'div'>>;
  fixedWidth?: number;
}

export const SearchBar = observer<SearchBarProps>(
  ({ trigger, fixedWidth, className, ...props }) => {
    const { t } = useTranslation();
    const {
      inputRef,
      portalInputRef,
      setSearchWrapperRef,
      setDropdownWrapperRef,
      queryString,
      isLoading,
      recommendationsData,
      isDropdownVisible,
      filteredSearchResults,
      handleStartSearch,
      handleContainerClick,
      handleKeyUp,
      handleInputChange,
      handleCategoryClick,
      handleSelectRecommendation,
      handleHistoryReset,
      handleCloseDropdown,
      handleExploreCatalog,
    } = useSearchBar();

    const [searchBarElement, setSearchBarElement] =
      useState<HTMLDivElement | null>(null);
    const mounted = useMounted();

    const handleRef = useCallback(
      (node: HTMLDivElement | null) => {
        setSearchWrapperRef(node);
        setSearchBarElement(node);
      },
      [setSearchWrapperRef],
    );

    const rect = searchBarElement?.getBoundingClientRect();

    const loadingIconClasses = cn(
      'h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-t-transparent',
      'border-navigation-icon-primary',
    );

    const searchIconClasses = cn(
      'shrink-0 cursor-pointer',
      'text-navigation-icon-primary',
    );

    const searchBarBaseClasses = cn([
      'relative z-1003 flex h-12 w-full min-w-[400px] max-w-[584px] cursor-text flex-row items-center justify-start gap-2 px-4 outline-none border-transparent',
      'rounded-full border-2 transition-all duration-200',
      'bg-navigation-surface hover:bg-navigation-surface-2',
    ]);

    const getSearchInputClasses = (placeholderHidden: boolean) =>
      cn([
        'h-full flex-1 border-none bg-transparent outline-none',
        '[font:var(--text-300-regular)]',
        'text-navigation-text-primary placeholder:text-navigation-text-tertiary',
        placeholderHidden && 'placeholder:opacity-0',
      ]);

    const overlayBg =
      '[background:linear-gradient(180deg,var(--surface-transparent-3)_0%,var(--surface-transparent-5)_100%)]';

    return (
      <>
        {mounted &&
          createPortal(
            <div
              className={cn(
                isDropdownVisible
                  ? 'pointer-events-auto'
                  : 'pointer-events-none opacity-0',
                'fixed inset-0 z-1000',
                '[-webkit-backdrop-filter:blur(9px)] [backdrop-filter:blur(9px)]',
                '[transition:var(--dropdown-opacity-transition)]',
                overlayBg,
              )}
              onClick={handleCloseDropdown}
            />,
            document.body,
          )}

        <div
          {...props}
          data-active={isDropdownVisible ? 'active' : ''}
          className={cn(
            trigger
              ? 'cursor-pointer'
              : 'relative z-1002 flex flex-[1_1_0] flex-row items-center justify-center bg-navigation-bg px-[6px]',
            className,
          )}
        >
          {trigger ? (
            cloneElement(trigger, {
              ref: handleRef,
              tabIndex: 0,
              onClick: handleContainerClick,
            })
          ) : (
            <div
              className={cn([
                searchBarBaseClasses,
                isDropdownVisible &&
                  'border-navigation-stroke-primary bg-navigation-bg',
              ])}
              ref={handleRef}
              tabIndex={0}
              onClick={handleContainerClick}
            >
              {isLoading ? (
                <div className={loadingIconClasses} />
              ) : (
                <Icon
                  onClick={handleStartSearch}
                  type="search-sm"
                  size={16}
                  className={searchIconClasses}
                />
              )}

              <input
                ref={inputRef}
                data-input="search"
                type="text"
                value={queryString}
                className={getSearchInputClasses(isDropdownVisible)}
                placeholder={t('header:searchPlaceholder')}
                onKeyUp={handleKeyUp}
                onChange={handleInputChange}
              />
            </div>
          )}
        </div>

        {mounted &&
          rect &&
          createPortal(
            <div
              ref={setDropdownWrapperRef}
              className={cn(
                'fixed z-1003 rounded-[32px] p-2',
                'bg-navigation-bg',
                trigger ? 'right-0 m-auto' : 'right-auto -m-2',
                isDropdownVisible ? 'block' : 'hidden',
              )}
              style={{
                top: `${trigger ? 16 : rect.top}px`,
                left: `${trigger ? 0 : rect.left}px`,
                width:
                  fixedWidth ||
                  (trigger ? 'fit-content' : `${rect.width + 16}px`),
              }}
            >
              <div
                className={cn([
                  searchBarBaseClasses,
                  'border-navigation-stroke-primary bg-navigation-bg',
                ])}
              >
                {isLoading ? (
                  <div className={loadingIconClasses} />
                ) : (
                  <Icon
                    onClick={handleStartSearch}
                    type="search-sm"
                    size={16}
                    className={searchIconClasses}
                  />
                )}

                <input
                  ref={portalInputRef}
                  data-input="search"
                  type="text"
                  value={queryString}
                  className={getSearchInputClasses(true)}
                  placeholder={t('header:searchPlaceholder')}
                  onKeyUp={handleKeyUp}
                  onChange={handleInputChange}
                  autoFocus
                />
              </div>

              <div
                className={cn([
                  'z-1001 w-full cursor-default overflow-y-auto px-2 pt-6 pb-2',
                  'max-h-[calc(100vh-100px)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
                  '[transition:var(--dropdown-opacity-transition),var(--dropdown-transform-transition)]',
                  'pointer-events-auto translate-y-0 scale-y-100 opacity-100',
                ])}
              >
                <SearchDropdownContent
                  recommendationsData={recommendationsData}
                  handleHistoryReset={handleHistoryReset}
                  handleSelectRecommendation={handleSelectRecommendation}
                  handleCategoryClick={handleCategoryClick}
                  handleCloseDropdown={handleCloseDropdown}
                  filteredSearchResults={filteredSearchResults}
                  queryString={queryString}
                  handleStartSearch={handleStartSearch}
                  handleExploreCatalog={handleExploreCatalog}
                />
              </div>
            </div>,
            document.body,
          )}
      </>
    );
  },
);
